import { useCallback, useEffect, useRef, useState } from 'react';
import { Eraser } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/shared/lib/cn';

/**
 * Componente local de captura de firma sobre <canvas>, sin dependencias
 * externas. Cubre mouse + touch + stylus vía pointer events y previene scroll
 * en tablet con `touch-action: none` + `preventDefault` durante el trazo.
 *
 * Devuelve la firma como dataURL PNG vía `onChange`. El cambio se emite con
 * un debounce de 200ms para no saturar al padre con cada pixel del trazo.
 */

export interface SignaturePadProps {
  onChange(dataUrl: string | null): void;
  width?: number;
  height?: number;
  strokeColor?: string;
  backgroundColor?: string;
  /** Texto del placeholder visible cuando el canvas está vacío. */
  placeholder?: string;
  className?: string;
}

export function SignaturePad({
  onChange,
  width,
  height = 180,
  strokeColor = '#0f172a',
  backgroundColor,
  placeholder = 'Firma aquí',
  className,
}: SignaturePadProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const isDrawingRef = useRef<boolean>(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onChangeRef = useRef(onChange);

  const [hasInk, setHasInk] = useState<boolean>(false);

  // Mantén la referencia siempre actualizada para no re-bindear listeners.
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Setup del canvas (resolución por DPR + estilos de trazo).
  // Volvemos a configurar si cambia el tamaño visual del contenedor.
  const setupCanvas = useCallback((): void => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (canvas === null || container === null) return;

    const dpr = window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;
    const rect = container.getBoundingClientRect();
    const cssWidth = width ?? rect.width;
    const cssHeight = height;

    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;
    canvas.width = Math.round(cssWidth * dpr);
    canvas.height = Math.round(cssHeight * dpr);

    const ctx = canvas.getContext('2d');
    if (ctx === null) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    if (backgroundColor !== undefined) {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, cssWidth, cssHeight);
    }
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 2.2;
    ctxRef.current = ctx;
  }, [width, height, strokeColor, backgroundColor]);

  useEffect(() => {
    setupCanvas();
    // Re-setup al resize por si el contenedor cambia (responsive).
    const handleResize = (): void => setupCanvas();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [setupCanvas]);

  const emitChange = useCallback((): void => {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      const canvas = canvasRef.current;
      if (canvas === null) return;
      try {
        const dataUrl = canvas.toDataURL('image/png');
        onChangeRef.current(dataUrl);
      } catch {
        onChangeRef.current(null);
      }
    }, 200);
  }, []);

  const pointFromEvent = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
      const canvas = canvasRef.current;
      if (canvas === null) return { x: 0, y: 0 };
      const rect = canvas.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
    },
    [],
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>): void => {
      event.preventDefault();
      const canvas = canvasRef.current;
      const ctx = ctxRef.current;
      if (canvas === null || ctx === null) return;
      try {
        canvas.setPointerCapture(event.pointerId);
      } catch {
        /* algunos navegadores rechazan setPointerCapture en mouse — OK */
      }
      isDrawingRef.current = true;
      const point = pointFromEvent(event);
      lastPointRef.current = point;
      ctx.beginPath();
      ctx.moveTo(point.x, point.y);
      // Dot inicial para taps muy cortos.
      ctx.lineTo(point.x + 0.01, point.y + 0.01);
      ctx.stroke();
      if (!hasInk) setHasInk(true);
    },
    [pointFromEvent, hasInk],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>): void => {
      if (!isDrawingRef.current) return;
      event.preventDefault();
      const ctx = ctxRef.current;
      if (ctx === null) return;
      const point = pointFromEvent(event);
      const last = lastPointRef.current;
      if (last !== null) {
        // Suavizado: trazo entre el punto anterior y el actual con curva cuadrática
        // tomando el midpoint como punto de control.
        const midX = (last.x + point.x) / 2;
        const midY = (last.y + point.y) / 2;
        ctx.quadraticCurveTo(last.x, last.y, midX, midY);
        ctx.stroke();
      }
      lastPointRef.current = point;
    },
    [pointFromEvent],
  );

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>): void => {
      if (!isDrawingRef.current) return;
      event.preventDefault();
      isDrawingRef.current = false;
      const ctx = ctxRef.current;
      if (ctx !== null) ctx.closePath();
      const canvas = canvasRef.current;
      if (canvas !== null) {
        try {
          canvas.releasePointerCapture(event.pointerId);
        } catch {
          /* noop */
        }
      }
      lastPointRef.current = null;
      emitChange();
    },
    [emitChange],
  );

  const handleClear = useCallback((): void => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (canvas === null || ctx === null) return;
    const dpr = window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    if (backgroundColor !== undefined) {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    }
    setHasInk(false);
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    onChangeRef.current(null);
  }, [backgroundColor]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative w-full rounded-md border bg-surface overflow-hidden',
        hasInk
          ? 'border-border-strong'
          : 'border-dashed border-border-strong',
        className,
      )}
      style={{ height }}
    >
      <canvas
        ref={canvasRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onPointerLeave={handlePointerUp}
        className="block w-full h-full cursor-crosshair"
        style={{ touchAction: 'none' }}
      />
      {!hasInk && (
        <div className="absolute inset-0 grid place-items-center pointer-events-none text-text-muted text-sm select-none">
          {placeholder}
        </div>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleClear}
        className="absolute top-2 right-2 h-7 px-2 text-xs"
        disabled={!hasInk}
      >
        <Eraser className="w-3.5 h-3.5" /> Limpiar
      </Button>
    </div>
  );
}
